import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@workspace/ui/components/collapsible'
import { cn } from '@workspace/ui/lib/utils'
import { ChevronDown } from 'lucide-react'
import { useState } from 'react'
import { MarkdownContent } from '@/components/markdown-content'
import type { IndividualMetricEvaluation } from '../types/pitch.types'
import { METRIC_LABELS, PERSONA_LABELS } from '../utils/scores'
import { ScoreBadge } from './score-badge'

interface JudgesAccordionProps {
  evaluations: IndividualMetricEvaluation[]
}

interface JudgeCardProps {
  persona: string
  evaluations: IndividualMetricEvaluation[]
  avgScore: number
}

function JudgeCard({ persona, evaluations, avgScore }: JudgeCardProps) {
  const [isOpen, setIsOpen] = useState(false)

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <CollapsibleTrigger className="flex w-full items-center justify-between p-4 hover:bg-card/80 transition-colors">
          <div className="flex items-center gap-3">
            <span className="font-medium">{PERSONA_LABELS[persona] ?? persona}</span>
          </div>
          <div className="flex items-center gap-3">
            <ScoreBadge score={avgScore} size="lg" />
            <ChevronDown
              className={cn(
                'size-4 text-muted-foreground transition-transform',
                isOpen && 'rotate-180'
              )}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border p-4 space-y-6">
            {evaluations
              .slice()
              .sort((a, b) => a.metric.localeCompare(b.metric))
              .map((evaluation) => (
                <div
                  key={evaluation.id}
                  className="border-b border-border pb-6 last:border-0 last:pb-0"
                >
                  <div className="mb-2 flex items-center justify-between">
                    <span className="font-medium text-sm">
                      {METRIC_LABELS[evaluation.metric] ?? evaluation.metric}
                    </span>
                    {evaluation.score !== null && <ScoreBadge score={evaluation.score} size="sm" />}
                  </div>
                  {evaluation.reasoning && (
                    <MarkdownContent>{evaluation.reasoning}</MarkdownContent>
                  )}
                </div>
              ))}
          </div>
        </CollapsibleContent>
      </div>
    </Collapsible>
  )
}

export function JudgesAccordion({ evaluations }: JudgesAccordionProps) {
  // Group evaluations by persona
  const groupedByPersona = evaluations.reduce(
    (acc, evaluation) => {
      const persona = evaluation.persona
      if (!acc[persona]) {
        acc[persona] = []
      }
      acc[persona].push(evaluation)
      return acc
    },
    {} as Record<string, IndividualMetricEvaluation[]>
  )

  // Calculate average score per persona
  const personaScores = Object.entries(groupedByPersona).map(([persona, evals]) => {
    const validScores = evals.filter((e) => e.score !== null)
    const avgScore = validScores.length
      ? Math.round(validScores.reduce((sum, e) => sum + (e.score ?? 0), 0) / validScores.length)
      : 0
    return { persona, evaluations: evals, avgScore }
  })

  return (
    <div className="space-y-4">
      {personaScores.map(({ persona, evaluations: evals, avgScore }) => (
        <JudgeCard key={persona} persona={persona} evaluations={evals} avgScore={avgScore} />
      ))}
    </div>
  )
}
